import type {
  ContainerState,
  DeploymentStatus,
  InstanceStatus,
  LicenseState,
  NodeStatus,
  StackStatus,
} from "../api/types";
import "./StatePill.css";

export type PillTone = "positive" | "neutral" | "warning" | "info" | "negative";
export type PillShape = "dot" | "ring" | "bars" | "spin" | "triangle";

interface StatePillProps {
  label: string;
  tone: PillTone;
  shape: PillShape;
}

/** Small inline glyphs so status reads by shape as well as color — an
 * operator with color-vision deficiency (or a black-and-white terminal
 * screenshot) can still tell "running" from "exited" from "paused." */
function Glyph({ shape }: { shape: PillShape }) {
  switch (shape) {
    case "dot":
      return (
        <svg viewBox="0 0 8 8" width="8" height="8" aria-hidden="true" focusable="false">
          <circle cx="4" cy="4" r="4" fill="currentColor" />
        </svg>
      );
    case "ring":
      return (
        <svg viewBox="0 0 8 8" width="8" height="8" aria-hidden="true" focusable="false">
          <circle cx="4" cy="4" r="3" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
    case "bars":
      return (
        <svg viewBox="0 0 8 8" width="8" height="8" aria-hidden="true" focusable="false">
          <rect x="1" y="0" width="2" height="8" fill="currentColor" />
          <rect x="5" y="0" width="2" height="8" fill="currentColor" />
        </svg>
      );
    case "spin":
      return (
        <svg
          viewBox="0 0 8 8"
          width="8"
          height="8"
          aria-hidden="true"
          focusable="false"
          className="state-pill__spin"
        >
          <path d="M4 0a4 4 0 1 1-3.5 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case "triangle":
      return (
        <svg viewBox="0 0 8 8" width="8" height="8" aria-hidden="true" focusable="false">
          <path d="M4 0 L8 8 L0 8 Z" fill="currentColor" />
        </svg>
      );
  }
}

export function StatePill({ label, tone, shape }: StatePillProps) {
  return (
    <span className={`state-pill state-pill--${tone}`}>
      <Glyph shape={shape} />
      {label}
    </span>
  );
}

const CONTAINER_STATE_META: Record<ContainerState, { label: string; tone: PillTone; shape: PillShape }> = {
  running: { label: "Running", tone: "positive", shape: "dot" },
  exited: { label: "Exited", tone: "neutral", shape: "ring" },
  paused: { label: "Paused", tone: "warning", shape: "bars" },
  restarting: { label: "Restarting", tone: "info", shape: "spin" },
  created: { label: "Created", tone: "neutral", shape: "ring" },
  dead: { label: "Dead", tone: "negative", shape: "triangle" },
};

export function ContainerStatePill({ state }: { state: ContainerState }) {
  const meta = CONTAINER_STATE_META[state];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

const STACK_STATUS_META: Record<StackStatus, { label: string; tone: PillTone; shape: PillShape }> = {
  deploying: { label: "Deploying", tone: "info", shape: "spin" },
  deployed: { label: "Deployed", tone: "positive", shape: "dot" },
  error: { label: "Error", tone: "negative", shape: "triangle" },
  // Stopped, but the data is still there (ADR-0020 §1b) — neutral, not an error.
  down: { label: "Down (data retained)", tone: "neutral", shape: "ring" },
};

export function StackStatusPill({ status }: { status: StackStatus }) {
  const meta = STACK_STATUS_META[status];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

const NODE_STATUS_META: Record<NodeStatus, { label: string; tone: PillTone; shape: PillShape }> = {
  online: { label: "Online", tone: "positive", shape: "dot" },
  offline: { label: "Offline", tone: "negative", shape: "triangle" },
  unknown: { label: "Unknown", tone: "neutral", shape: "ring" },
};

export function NodeStatusPill({ status }: { status: NodeStatus }) {
  const meta = NODE_STATUS_META[status];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

// `Service.status` is server-derived infrastructure state, not a fixed
// client-known enum (see the doc comment on `Service` in types.ts) — today
// it is only ever `"created"` (deploying isn't implemented). Known values
// get real styling; anything else falls back to a plain neutral pill
// showing the server's own string rather than guessing at meaning.
const SERVICE_STATUS_META: Record<string, { label: string; tone: PillTone; shape: PillShape }> = {
  created: { label: "Created", tone: "neutral", shape: "ring" },
  deploying: { label: "Deploying", tone: "info", shape: "spin" },
  running: { label: "Running", tone: "positive", shape: "dot" },
  stopped: { label: "Stopped", tone: "neutral", shape: "ring" },
  error: { label: "Error", tone: "negative", shape: "triangle" },
};

export function ServiceStatusPill({ status }: { status: string }) {
  const meta = SERVICE_STATUS_META[status] ?? { label: status, tone: "neutral" as const, shape: "ring" as const };
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

const DEPLOYMENT_STATUS_META: Record<DeploymentStatus, { label: string; tone: PillTone; shape: PillShape }> = {
  pending: { label: "Pending", tone: "neutral", shape: "ring" },
  provisioning: { label: "Provisioning", tone: "info", shape: "spin" },
  deploying: { label: "Deploying", tone: "info", shape: "spin" },
  running: { label: "Running", tone: "positive", shape: "dot" },
  failed: { label: "Failed", tone: "negative", shape: "triangle" },
  rolled_back: { label: "Rolled back", tone: "warning", shape: "bars" },
};

export function DeploymentStatusPill({ status }: { status: DeploymentStatus }) {
  const meta = DEPLOYMENT_STATUS_META[status];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

const INSTANCE_STATUS_META: Record<InstanceStatus, { label: string; tone: PillTone; shape: PillShape }> = {
  requested: { label: "Requested", tone: "neutral", shape: "ring" },
  provisioning: { label: "Provisioning", tone: "info", shape: "spin" },
  running: { label: "Running", tone: "positive", shape: "dot" },
  deleting: { label: "Deleting", tone: "warning", shape: "bars" },
  deleted: { label: "Deleted", tone: "neutral", shape: "ring" },
  error: { label: "Error", tone: "negative", shape: "triangle" },
};

export function InstanceStatusPill({ status }: { status: InstanceStatus }) {
  const meta = INSTANCE_STATUS_META[status];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

// `stale`/`absent` are deliberately "neutral" tone, not "warning" — neither
// is alarming on its own (see entitlementRules.ts for the fuller rationale:
// `stale` means still-fully-licensed, `absent` is the ordinary self-hosted
// day-one state). `expired`/`invalid` ARE meant to read as trouble.
const LICENSE_STATE_META: Record<LicenseState, { label: string; tone: PillTone; shape: PillShape }> = {
  active: { label: "Active", tone: "positive", shape: "dot" },
  stale: { label: "Stale (still licensed)", tone: "neutral", shape: "ring" },
  expired: { label: "Expired", tone: "warning", shape: "triangle" },
  absent: { label: "No license", tone: "neutral", shape: "ring" },
  invalid: { label: "Invalid", tone: "negative", shape: "triangle" },
};

export function LicenseStatePill({ state }: { state: LicenseState }) {
  const meta = LICENSE_STATE_META[state];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}
